import { Box, BoxProps, HStack, Link, Text } from "@chakra-ui/react";
import { useMemo } from "react";

import {
  DeleteIconButton,
  SimpleIconButton,
} from "@/common/components/CommonIconButtons";
import CompanyDisplay from "@/common/components/CompanyDisplay";
import { DataTable } from "@/common/components/DataTable/DataTable";
import ExpandableTextCell from "@/common/components/ExpandableTextCell";
import NotAvailableCell from "@/common/components/NotAvailableCell";
import TimestampCell from "@/common/components/TimestampCell";
import { UserDisplay } from "@/common/components/UserDisplay";
import { useSafeDataTableStateProps } from "@/common/hooks/useDataTableParamState";
import { createSortableColumnHelper } from "@/common/types/reactTableHelpers";
import { FeatureDisplay } from "@/feature/components/FeatureDisplay";
import DeleteFeedbackDialog from "@/feedback/components/DeleteFeedbackDialog";
import { FeedbackEmptyState } from "@/feedback/components/FeedbackEmptyState";
import { FeedbackFilters } from "@/feedback/components/FeedbackFilters";
import PushFeedbackDialog from "@/feedback/components/PushFeedbackDialog";
import SatisfactionCell from "@/feedback/components/SatisfactionCell";
import {
  defaultFeedbackColumns,
  useFeedbackTableState,
} from "@/feedback/hooks/useFeedbackTableState";
import { LinearConnectionIcon } from "@/integration/components/linear/LinearConnectionIcon";
import { useFlag } from "@reflag/react-sdk";
import { FEEDBACK_SOURCE_LABELS } from "@reflag/shared/constants";
import { FunnelStepList } from "@reflag/shared/featureAPI";
import {
  FeedbackDTO,
  FeedbackList,
  FeedbackListSortBy,
  FeedbackSource,
} from "@reflag/shared/feedbackAPI";

export const cellVerticalAdjust: BoxProps = {
  display: "inline-flex",
  alignItems: "center",
  minH: 7,
};

type Props = {
  data?: FeedbackList;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  context?: "root" | "company";
  onRetry?: () => void;
};

export const FeedbackTable = ({
  data,
  isLoading,
  isFetching,
  isError,
  context = "root",
  onRetry,
}: Props) => {
  const { isEnabled: linearIntegrationFeedbackEnabled } = useFlag(
    "linear-integration-feedback",
  );
  const tableState = useFeedbackTableState();
  // We don't want to pass along invalid props to the data table
  const safeTableProps = useSafeDataTableStateProps(tableState);

  const isFiltered =
    tableState.funnelStepsFilter.length < FunnelStepList.length ||
    !!tableState.satisfactionFilter ||
    !!tableState.featureFilter ||
    !!tableState.companyFilter;

  const columnHelper = createSortableColumnHelper<
    FeedbackDTO,
    FeedbackListSortBy
  >();

  const columns = useMemo(
    () => [
      columnHelper.accessor("score", {
        header: "Score",
        cell: (cell) => {
          const value = cell.getValue();
          return (
            <Box {...cellVerticalAdjust}>
              {value !== null ? (
                <SatisfactionCell value={value} />
              ) : (
                <NotAvailableCell />
              )}
            </Box>
          );
        },
      }),
      columnHelper.accessor("question", {
        header: "Question",
        enableSorting: false,
        cell: (cell) => {
          const value = cell.getValue();
          return (
            <Box {...cellVerticalAdjust}>
              {value ? (
                <ExpandableTextCell fontSize="sm" value={value} />
              ) : (
                <NotAvailableCell />
              )}
            </Box>
          );
        },
      }),
      columnHelper.accessor("comment", {
        header: "Comment",
        cell: (cell) => {
          const value = cell.getValue();
          return (
            <Box {...cellVerticalAdjust}>
              {value ? (
                <ExpandableTextCell fontSize="sm" value={value} />
              ) : (
                <NotAvailableCell />
              )}
            </Box>
          );
        },
        minSize: 280,
      }),
      columnHelper.accessor("featureName", {
        header: "Flag",
        enableSorting: false,
        cell: ({ row }) => (
          <Box {...cellVerticalAdjust}>
            {row.original.featureId ? (
              <FeatureDisplay
                feature={{
                  id: row.original.featureId,
                  name: row.original.featureName || "",
                  icon: row.original.featureIcon,
                  iconColor: row.original.featureIconColor,
                }}
                fontSize="sm"
                link
              />
            ) : (
              <NotAvailableCell />
            )}
          </Box>
        ),
      }),
      columnHelper.accessor("userName", {
        header: "User",
        cell: ({ row }) => (
          <Box {...cellVerticalAdjust}>
            {row.original.userId ? (
              <UserDisplay
                fontSize="sm"
                link
                user={{
                  id: row.original.userId,
                  name: row.original.userName,
                  email: row.original.userEmail,
                  avatarUrl: row.original.userAvatarUrl,
                }}
              />
            ) : (
              <NotAvailableCell />
            )}
          </Box>
        ),
      }),
      columnHelper.accessor("userEmail", {
        header: "User email",
        cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <Box {...cellVerticalAdjust}>
              {value ? (
                <Link fontSize="sm" href={`mailto:${value}`}>
                  {value}
                </Link>
              ) : (
                <NotAvailableCell />
              )}
            </Box>
          );
        },
      }),
      columnHelper.accessor("companyName", {
        header: "Company",
        cell: ({ row }) => (
          <Box {...cellVerticalAdjust}>
            {row.original.companyId ? (
              <CompanyDisplay
                company={{
                  id: row.original.companyId,
                  name: row.original.companyName,
                  avatarUrl: row.original.companyAvatarUrl,
                }}
                fontSize="sm"
                link
              />
            ) : (
              <NotAvailableCell />
            )}
          </Box>
        ),
      }),
      columnHelper.accessor("source", {
        header: "Source",
        enableSorting: false,
        cell: (cell) => {
          const value = cell.getValue();
          const name = FEEDBACK_SOURCE_LABELS[value as FeedbackSource];
          if (!name) return <NotAvailableCell />;

          return (
            <Box {...cellVerticalAdjust}>
              <Text fontSize="sm">{name}</Text>
            </Box>
          );
        },
      }),
      columnHelper.accessor("timestamp", {
        header: "Received",
        sortDescFirst: true,
        cell: (cell) => {
          const value = cell.getValue();
          return (
            <Box {...cellVerticalAdjust}>
              {value !== undefined ? (
                <TimestampCell value={value} leftAlign />
              ) : (
                <NotAvailableCell />
              )}
            </Box>
          );
        },
      }),
      columnHelper.display({
        header: "Actions",
        id: "actions",
        enableSorting: false,
        cell: ({ row }) => (
          <Box {...cellVerticalAdjust}>
            <HStack spacing={0}>
              <DeleteFeedbackDialog feedbackId={row.original.id}>
                {({ openDialog }) => (
                  <DeleteIconButton
                    label="Delete feedback entry"
                    onClick={openDialog}
                  />
                )}
              </DeleteFeedbackDialog>
              {linearIntegrationFeedbackEnabled &&
                row.original.featureLinearTrackingEntity && (
                  <PushFeedbackDialog
                    feedbackId={row.original.id}
                    provider="linearComment"
                  >
                    {({ openDialog }) => (
                      <SimpleIconButton
                        icon={<LinearConnectionIcon />}
                        label="Push to Linear"
                        onClick={openDialog}
                      />
                    )}
                  </PushFeedbackDialog>
                )}
            </HStack>
          </Box>
        ),
      }),
    ],
    [columnHelper, linearIntegrationFeedbackEnabled],
  );

  const feedback = data?.data || [];

  return (
    <DataTable
      _toolbarLeft={{ wrap: "wrap", alignSelf: "flex-start" }}
      _toolbarRight={{ alignSelf: "flex-start" }}
      columns={columns}
      columnVerticalAlign="top"
      data={feedback}
      defaultColumns={defaultFeedbackColumns}
      emptyState={
        <FeedbackEmptyState
          isError={isError}
          isFiltered={isFiltered}
          isLoading={isLoading}
          onRetry={onRetry}
        />
      }
      isFetching={isFetching}
      meta={data?.metadata}
      pageSize={20}
      tableId={context === "root" ? "feedback" : `${context}-feedback`}
      toolbarLeftActions={() => (
        <FeedbackFilters
          canFilterByCompany={context !== "company"}
          canFilterBySegment={context !== "company"}
        />
      )}
      totalCount={data?.totalCount ?? 0}
      canCustomize
      canPaginate
      canSort
      {...safeTableProps}
    />
  );
};
